import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Stack,
  Chip,
  Button,
  Grid,
  alpha,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert as MuiAlert
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import LockIcon from '@mui/icons-material/Lock';
import { processLatexString } from '../../../components/LatexRenderer';
import demoService from '../../../services/demoApi';
import DemoCompletionCTA from './DemoCompletionCTA';

const DemoExamDisplay = ({ examData, onCTAClick, onBackToLanding, demoAnalytics }) => {
  const theme = useTheme();
  const [imageMap, setImageMap] = useState({});
  const [questionCount, setQuestionCount] = useState(0);
  const [pdfStatus, setPdfStatus] = useState('generating');
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [showSolutionsTeaser, setShowSolutionsTeaser] = useState(false);
  
  useEffect(() => {
    // Fetch demo images when component mounts
    const fetchDemoImages = async () => {
      try {
        const images = await demoService.getImages();
        const map = {};
        images.forEach(img => {
          map[img.filename] = img.id;
          const baseFilename = img.filename.replace(/\.[^/.]+$/, '');
          map[baseFilename] = img.id;
        });
        setImageMap(map);
        setImagesLoaded(true);
      } catch (error) {
        console.error('Error fetching demo images:', error);
        setImagesLoaded(true); // Set to true even on error to avoid blocking UI
      }
    };

    fetchDemoImages();
  }, []);
  
  // Poll for PDF status
  useEffect(() => {
    if (!examData?.exam_id) return;
    
    
    const checkPdfStatus = async () => {
      try {
        const response = await demoService.checkPdfStatus(examData.exam_id);
        setPdfStatus(response.status);
        
        // Stop polling if PDF is ready or failed
        if (response.status === 'ready' || response.status === 'failed') {
          clearInterval(intervalId);
        }
      } catch (error) {
        console.error('Failed to check PDF status:', error);
      }
    };
    
    // Check immediately
    checkPdfStatus();
    
    // Then poll every 3 seconds
    const intervalId = setInterval(checkPdfStatus, 3000);
    
    return () => clearInterval(intervalId);
  }, [examData?.exam_id]);
  
  useEffect(() => {
    // Calculate total question count for progress CTAs
    const mcqCount = examData?.questions?.mcq_questions?.length || 0;
    const extendedCount = examData?.questions?.extended_questions?.length || 0;
    setQuestionCount(mcqCount + extendedCount);
    
    // Track exam viewing in demo analytics
    if (demoAnalytics && examData) {
      // Track each question as "viewed" for exam mode
      const totalQuestions = mcqCount + extendedCount;
      for (let i = 1; i <= totalQuestions; i++) {
        const questionType = i <= mcqCount ? 'mcq' : 'extended';
        demoAnalytics.trackQuestionAnswered({
          type: questionType,
          questionNumber: i,
          questionId: `exam_q_${i}`,
          isCorrect: true, // Exam mode doesn't have right/wrong
          userAnswer: 'viewed',
          correctAnswer: 'n/a'
        });
      }
    }
  }, [examData, demoAnalytics]);
  
  // Show loading state while images are being fetched
  if (!imagesLoaded) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', textAlign: 'center', py: 8 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3 }}>
          Loading exam resources...
        </Typography>
      </Box>
    );
  }
  
  if (!examData || !examData.questions) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No exam data available
        </Typography>
        <Button onClick={onBackToLanding} sx={{ mt: 2 }}>
          Return to Generator
        </Button>
      </Box>
    );
  }

  const { questions } = examData;
  const { mcq_questions = [], extended_questions = [] } = questions;
  const totalMarks = mcq_questions.length + extended_questions.reduce((sum, q) => sum + (q.marks || 0), 0);
  
  const renderMCQQuestion = (question, index) => {
    let questionText = '';
    let options = [];
    
    // Use templated content if available
    if (question.templated_content) {
      questionText = question.templated_content.question || '';
      options = ['a', 'b', 'c', 'd'].map(opt => ({
        label: opt.toUpperCase(),
        text: question.templated_content[`option_${opt}`] || ''
      })).filter(opt => opt.text);
    } else {
      // Fall back to parsing raw content
      const content = question.content || '';
      const parts = content.split('\\choice');
      questionText = parts[0].replace(/\\question\[\d+\]/, '').trim();
      
      options = parts.slice(1).map((opt, i) => {
        let optText = opt.trim();
        optText = optText.replace(/\\end\{oneparchoices\}.*$/s, '').trim();
        return {
          label: String.fromCharCode(65 + i), // A, B, C, D
          text: optText
        };
      }).filter(opt => opt.text);
    }

    return (
      <Paper 
        key={question.id} 
        sx={{ 
          p: 4, 
          mb: 4,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          '&:hover': {
            borderColor: theme.palette.primary.light,
            boxShadow: theme.shadows[2],
          },
          transition: 'all 0.3s ease'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Question {index + 1}
          </Typography>
          <Chip 
            label="1 mark" 
            size="small" 
            color="primary"
            sx={{ fontWeight: 500 }}
          />
        </Box>
        
        <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
          {processLatexString(questionText, 'demo', imageMap, 'mcq-question')}
        </Typography>
        
        <Grid container spacing={2}>
          {options.map((option, index) => (
            <Grid item xs={12} sm={6} key={option.label}>
              <Box 
                sx={{ 
                  p: 2, 
                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  borderRadius: 1,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'flex-start'
                }}
              >
                <Typography variant="body1" sx={{ display: 'flex' }}>
                  <Box component="span" sx={{ fontWeight: 600, mr: 1, flexShrink: 0 }}>
                    {option.label}.
                  </Box>
                  <Box component="span">
                    {processLatexString(option.text, 'demo', imageMap, 'mcq-option')}
                  </Box>
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  };

  const renderExtendedQuestion = (question, index) => {
    let cleanContent = '';
    
    // Check if this is a multi-part question
    const hasParts = question.templated_content?.has_parts && question.templated_content?.parts?.length > 0;
    
    // For extended_parts questions, the main content might be empty
    if (question.template_style === 'extended_parts' && hasParts) {
      // Use templated content if available
      if (question.templated_content && question.templated_content.question) {
        cleanContent = question.templated_content.question;
      }
      // If no templated content or it's empty, that's fine - we'll just show the parts
    } else {
      // For regular extended questions
      if (question.templated_content && question.templated_content.question) {
        cleanContent = question.templated_content.question;
      } else {
        // Fall back to parsing raw content
        const content = question.content || '';
        cleanContent = content.replace(/\\question\[\d+\]/, '').trim();
      }
    }

    return (
      <Paper 
        key={question.id} 
        sx={{ 
          p: 4, 
          mb: 4,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          '&:hover': {
            borderColor: theme.palette.primary.light,
            boxShadow: theme.shadows[2],
          },
          transition: 'all 0.3s ease'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Question {mcq_questions.length + index + 1}
          </Typography>
          <Chip 
            label={`${question.marks} marks`} 
            size="small" 
            color="secondary"
            sx={{ fontWeight: 500 }}
          />
        </Box>
        
        {/* Only render main content if it exists and is not empty */}
        {cleanContent && cleanContent.trim() && (
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
            {processLatexString(cleanContent, 'demo', imageMap)}
          </Typography>
        )}
        
        {/* Render parts if this is a multi-part question */}
        {hasParts && (
          <Box sx={{ ml: 2, mb: 3 }}>
            {question.templated_content.parts.map((part, partIndex) => (
              <Box key={partIndex} sx={{ mb: 4 }}>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                  <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>
                    ({String.fromCharCode(96 + part.part_number)})
                  </Box>
                  {processLatexString(part.content, 'demo', imageMap)}
                  {part.marks && (
                    <Chip 
                      size="small" 
                      label={`${part.marks} mark${part.marks > 1 ? 's' : ''}`} 
                      sx={{ ml: 2, verticalAlign: 'middle' }} 
                    />
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Header - more compact like original */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          {questions.title || 'HSC Practice Exam - Demo'}
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
          {questions.subtitle || 'Generated by ExamGenerator.ai'}
        </Typography>
        
        {/* PDF Download Buttons */}
        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
          {pdfStatus === 'ready' && (
            <>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={async () => {
                  try {
                    // Fetch the PDF as a blob using the demo service
                    const response = await fetch(`/api/demo/download/${examData.exam_id}`, {
                      method: 'GET',
                      headers: {
                        'Accept': 'application/pdf',
                      },
                    });
                    
                    if (!response.ok) {
                      throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    
                    // Get the blob from the response
                    const blob = await response.blob();
                    
                    // Create a temporary URL for the blob
                    const url = window.URL.createObjectURL(blob);
                    
                    // Create a temporary anchor element and trigger download
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `demo_exam_${examData.exam_id}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    
                    // Clean up
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                  } catch (error) {
                    console.error('Error downloading PDF:', error);
                    alert('Failed to download PDF. Please try again.');
                  }
                }}
                sx={{ textTransform: 'none' }}
              >
                Download PDF
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={() => setShowSolutionsTeaser(true)}
                sx={{ 
                  textTransform: 'none',
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  }
                }}
              >
                Download Full Worked Solutions
              </Button>
            </>
          )}
          {pdfStatus === 'generating' && (
            <Button
              disabled
              startIcon={<CircularProgress size={20} color="inherit" />}
              sx={{ textTransform: 'none' }}
            >
              Generating PDF...
            </Button>
          )}
          {pdfStatus === 'failed' && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              PDF generation failed. Try refreshing the page.
            </Typography>
          )}
        </Stack>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* MCQ Section */}
      {mcq_questions.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Section A: Multiple Choice ({mcq_questions.length} marks)
          </Typography>
          {mcq_questions.map((q, i) => renderMCQQuestion(q, i))}
        </Box>
      )}

      {/* Extended Section */}
      {extended_questions.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Section B: Extended Response ({extended_questions.reduce((sum, q) => sum + (q.marks || 0), 0)} marks)
          </Typography>
          {extended_questions.map((q, i) => renderExtendedQuestion(q, i))}
        </Box>
      )}
      
      {/* CTA at bottom of exam */}
      <DemoCompletionCTA 
        onCTAClick={() => onCTAClick('completion', 'exam_view_bottom')}
        examData={examData}
        mode="view"
        questionsCompleted={questionCount}
      />
      
      {/* Solutions Teaser Snackbar */}
      <Snackbar
        open={showSolutionsTeaser}
        autoHideDuration={6000}
        onClose={() => setShowSolutionsTeaser(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert 
          onClose={() => setShowSolutionsTeaser(false)}
          severity="info"
          sx={{ width: '100%' }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => {
                setShowSolutionsTeaser(false);
                onCTAClick('solutions_teaser', 'exam_view');
              }}
            >
              Get Full Access
            </Button>
          }
        >
          Full worked solutions with step-by-step explanations are available in the full app
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default DemoExamDisplay;